<script setup>
// app\javascript\dashboard\components-next\message\bubbles\Base.vue
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { escapeHtml } from 'shared/helpers/HTMLSanitizer';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION, MESSAGE_TYPES } from '../constants';

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  groupWithPrevious,
  sender,
  messageType,
  isGroupConversation,
} = useMessageContext();
const { t } = useI18n();

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.PHONE]: 'bg-n-teal-4 text-n-slate-12', // Verde/teal para WhatsApp Celular
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
  [MESSAGE_VARIANTS.CALL]: 'bg-transparent',
  [MESSAGE_VARIANTS.VOICE]: '',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) {
    // O conteúdo é do CONTATO e vai pra `v-html`: escapa TUDO antes das regex
    // de markdown, senão uma mensagem citada com tag de evento executava no
    // origin do painel (XSS armazenado; auditoria 2026-09-22, F1). O markdown
    // abaixo só emite as tags que ele mesmo escreve.
    let html = escapeHtml(content);

    // Negrito: **texto** ou *texto*
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*([^*]+?)\*/g, '<strong>$1</strong>');

    // Itálico: _texto_
    html = html.replace(/_([^_]+?)_/g, '<em>$1</em>');

    // Sublinhado: ~texto~
    html = html.replace(/~([^~]+?)~/g, '<u>$1</u>');

    // Tachado: ~~texto~~
    html = html.replace(/~~(.+?)~~/g, '<s>$1</s>');

    // Código: `texto`
    html = html.replace(/`([^`]+?)`/g, '<code>$1</code>');

    return html;
  }

  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});

// ✅ NOVO: Detectar se deve mostrar nome do sender (para grupos)
const shouldShowSenderName = computed(() => {
  // Só mostrar para mensagens incoming (usuário)
  if (messageType.value !== MESSAGE_TYPES.INCOMING) return false;

  // Nome no PRIMEIRO balão de uma sequência do mesmo autor (o nome fica em cima do
  // balão). Antes era no último: numa sequência A, B, B, o B do meio parecia do A.
  return isGroupConversation.value && !groupWithPrevious.value;
});

const senderName = computed(() => {
  const name = sender.value?.name || '';
  return name;
});

const senderAvatar = computed(() => {
  return sender.value?.thumbnail || sender.value?.avatar || '';
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <!-- ✅ NOVO: Avatar e nome do sender em grupos (estilo WhatsApp) -->
    <div v-if="shouldShowSenderName" class="flex items-center gap-2 mb-2 px-1">
      <img
        v-if="senderAvatar"
        :src="senderAvatar"
        :alt="senderName"
        class="w-6 h-6 rounded-full object-cover flex-shrink-0"
      />
      <div
        v-else
        class="w-6 h-6 rounded-full bg-n-slate-6 flex items-center justify-center flex-shrink-0"
      >
        <span class="text-[10px] font-semibold text-n-slate-11">
          {{ senderName.charAt(0).toUpperCase() }}
        </span>
      </div>
      <span class="text-xs font-semibold text-n-slate-11 truncate">
        {{ senderName }}
      </span>
    </div>

    <div
      v-if="inReplyTo"
      class="bg-n-alpha-black1 rounded-lg p-2 -mx-1 mb-2 cursor-pointer"
      @click="scrollToMessage"
    >
      <span class="line-clamp-2 break-all" v-html="replyToPreview" />
    </div>
    <slot />
    <MessageMeta
      v-if="variant !== MESSAGE_VARIANTS.ACTIVITY"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
        shouldGroupWithNext ? 'opacity-70 text-[10px]' : '',
      ]"
      class="mt-2"
    />
  </div>
</template>
